import { defineComponent as _defineComponent } from 'vue'
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'

  import KetchUpButton from '@/components/common/KetchUpButton.vue'
  import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
  import Badge from '@/components/common/Badge.vue'
  import SVGRenderer from '@/components/common/SVGRenderer.vue'
  import Dropdown from '@/components/common/Dropdown.vue'
  import Drag from '@/components/common/Drag.vue'
  import StartCourseExamCTA from '@/components/course/StartCourseExamCTA.vue'
  import EditorButtons from '@/components/editor/EditorButtons.vue'
  import CourseSearch from '@/components/course/CourseSearch.vue'
  import CourseExamDate from '@/components/course/CourseExamDate.vue'
  import GoogleReviewWidget from '@/components/GoogleReviewWidget.vue'

  import { useRoute, useRouter } from 'vue-router/composables'
  import useIcons from '@/composables/useIcons'
  import useDuration from '@/composables/useDuration'
  import useCommonMixin from '@/composables/useCommonMixin'
  import useCourse from '@/composables/useCourse'
  import useSegment from '@/composables/useSegment'
  import useI18n from '@/composables/useI18n'
  import useBreakpoint from '@/composables/useBreakpoint'
  import useEditor from '@/composables/useEditor'
  import useShopifyClient from '@/composables/useShopifyClient'

  import { CourseModule } from '@/store/modules/course'
  import { EditorModule } from '@/store/modules/editor'

  import CourseApi from '@/services/api/CourseApi'
  import type { DropdownItem } from '@/services/interfaces/Common'
  import type {
    CourseModule as CourseModuleState,
    Lesson,
    CourseMap,
    EditorStatePayload,
  } from '@/services/interfaces/Course'
  import type { Nullable } from '@/services/interfaces/Content'

  import { v4 as uuidv4 } from 'uuid'
  import CookieHelper from '@/helpers/CookieHelper'
  import eventBus from '@/main'

  
export default /*#__PURE__*/_defineComponent({
  __name: 'ModuleLessonsSelector',
  emits: ['next-lesson-action', 'module-action', 'show-content', 'current-course-group-id'],
  setup(__props, { emit }) {

  

  const route = useRoute()
  const router = useRouter()

  const { translateString } = useI18n()
  const {
    moduleIcon,
    durationIcon,
    arrowRight,
    arrowDown,
    open,
    lessonCompletedIcon,
    activeLessonIcon,
    quizIcon,
    courseCertIconWhite,
    bigDownloadIcon,
    lockIcon,
    michaelAvatar,
    phoneIcon,
    pencilIcon,
    chrome,
    edge,
    closeIcon,
    plusIcon,
  } = useIcons()
  const { formatDurationToHHMM } = useDuration()
  const { convertToRoman, isSachkundeCompany, isTauresCompany } = useCommonMixin()
  const { isDesktopDevice, isLargeDesktop, isMobileDevice } = useBreakpoint()
  const { createShopifyCheckout } = useShopifyClient()
  const {
    course,
    currentCourseId,
    moduleId,
    lessonId,
    currentModule,
    currentLesson,
    courseModules,
    studyingStatus,
    quizMetaCount,
    isPurchasedCourse,
    isTrialCourse,
    guidedQuizDuration,
    nextEnabledModule,
    nextLesson,
    nextLessonIndex,
    fetchingEditorDependencies,
    showNextCTA,
    nextSiblingModule,
    nextCourseModule,
    onLessonPage,
    isTrialExpired,
  } = useCourse()
  const { coursePurchaseIntended } = useSegment()
  const { inEditorMode, isNewLesson, isNewModule, currentEditorModule } = useEditor(route)

  const moduleOpenStatus = reactive({ moduleId: '', open: false })
  const dropModuleTarget = ref(null as Nullable<CourseModuleState>)
  const dragModuleSource = ref(null as Nullable<CourseModuleState>)
  const dropLessonTarget = ref('')
  const dragLessonSourceIndex = ref(-1)
  const dragModuleSourceIndex = ref(-1)
  const isDraggingQuestion = ref(false)
  const loadingShopifyCheckout = ref(false)
  const hoveringIndex = ref(-1)

  const fakeModulePayload: CourseModuleState = {
    id: '123',
    name: '',
    description: '',
    teaserVideoUrl: '',
    teaserImageUrl: '',
    thumbnailTimestamp: null,
    credits: '',
    authors: [],
    orderingNumber: 0,
    isFree: false,
    benefits: [],
    learnings: null,
    lessons: [],
    courseGroupId: null,
    courseGroupOrderingNumber: 0,
    guidedQuiz: null,
    hasTiles: false,
    enableGuidedQuiz: false,
    disabled: false,
    duration: 0,
    numberOfLessons: 0,
  }

  const fakeLessonPayload: Lesson = {
    id: '123',
    title: '',
    orderingNumber: 0,
    duration: 0,
    type: 'Fake',
    completed: false,
    introText: '',
    outroText: '',
    videoUrl: null,
    videoTranscript: null,
    thumbnailTimestamp: 0,
    content: '',
    profileImageUrl: null,
    quiz: null,
    audioFiles: [],
    isOnePager: false,
    notes: null,
    hasNote: false,
    lessonTypeId: '',
    slideShow: [],
    downloadables: {
      downloadUrl: '',
      downloadTeaserImageUrl: '',
      audioUrl: '',
      audioAuthor: null,
      realDownloadData: '',
      downloadTeaserData: '',
    },
    leuckentext: null,
    is_copied_to_webflow: false,
    pending_webflow_update: false,
    updatedAt: '',
  }

  const ctaStatus = computed(() => {
    const currentModuleLessons = currentModule.value?.lessons
    const currentLessonIndex = currentModuleLessons?.findIndex((lesson) => {
      return lesson.id === currentLesson.value?.id
    })

    const nextModule = nextCourseModule(currentCourseGroupId.value)

    if (isPurchasedCourse.value) {
      const siblingIsFree = nextSiblingModule.value(currentCourseId.value, currentCourseGroupId.value)?.isFree

      if (currentLesson.value?.id && currentModuleLessons) {
        if (currentLessonIndex === currentModuleLessons!.length - 1) {
          if (currentModule.value.guidedQuiz) {
            return translateString('courses.sideshow.toGuidedQuiz')
          }
          if (isTrialCourse.value && !siblingIsFree) {
            return translateString('courses.buyCourse')
          }
          if (nextModule) {
            return translateString('courses.sideshow.nextModule')
          }
        }
        return translateString('courses.sideshow.nextLesson')
      } else if (route.name === 'GuidedQuiz') {
        if (isTrialCourse.value && !siblingIsFree) {
          return translateString('courses.buyCourse')
        }
        if (nextModule) {
          return translateString('courses.sideshow.nextModule')
        }
      } else if (!currentModuleLessons.length) {
        if (nextModule) {
          return translateString('courses.sideshow.nextModule')
        }
      }
      return translateString('courses.sideshow.startModule')
    }
    return translateString('courses.buyCourse')
  })

  const shouldBuyCourse = computed(() => !course.value?.purchased)

  const isLockedModule = computed(() => (isFree: boolean) => {
    return !isPurchasedCourse.value && !inEditorMode.value && !isFree && !course.value?.expired
  })

  const currentCourseGroupId = computed(() => CourseModule.currentCourseGroupId)

  const selectedCourseGroup = ref('')
  const courseGroupDropdownItems = computed((): DropdownItem[] => {
    return (
      course.value?.courseGroups?.map((group) => ({
        selectable:
          !!CourseModule.courseModules(currentCourseId.value, group.id).filter((mod) => !mod.disabled).length ||
          inEditorMode.value,
        type: group.id,
        title: group.name,
      })) || []
    )
  })

  const filteredCourseModules = computed(() => {
    if (EditorModule.editMode && !fetchingEditorDependencies.value) {
      if (!courseGroupDropdownItems.value.length) {
        const modules = EditorModule.coursesModuleMap[currentCourseId.value] || []

        if (modules.some((m) => m.id === '123')) return modules
        modules.unshift(fakeModulePayload)
        return modules
      }
      const modules = EditorModule.courseModules(currentCourseId.value, selectedCourseGroup.value) || []
      if (modules.some((m) => m.id === '123')) return modules
      modules.unshift(fakeModulePayload)
      return modules
    }
    if (!courseGroupDropdownItems.value) return courseModules.value
    return CourseModule.courseModules(currentCourseId.value, selectedCourseGroup.value)
  })

  const moduleCount = computed(() => {
    if (filteredCourseModules.value?.some((m) => m.id === '123') && !isNewModule.value) {
      return filteredCourseModules.value?.filter((m) => m.id !== '123').length
    }
    return filteredCourseModules.value?.length
  })

  const hasSomeFreeModules = computed(() => filteredCourseModules.value?.some((m) => m.isFree))

  const showTalkToExpert = computed(
    () => hasSomeFreeModules.value && course.value?.talkToExpert && !isPurchasedCourse.value,
  )

  const isSpecificModuleNew = computed(() => (moduleId: string) => (moduleId ? moduleId.startsWith('new-') : false))

  const isSpecificLessonNew = computed(() => (lessonId: string) => (lessonId ? lessonId.startsWith('new-') : false))

  const showStartExamCTA = computed(() => {
    return isDesktopDevice.value && course.value?.exam && route.name !== 'ExamStartPage'
  })

  const selectorAction = async () => {
    if (isPurchasedCourse.value) {
      if (
        route.name === 'GuidedQuiz' ||
        (currentModule.value.id && currentLesson.value?.id) ||
        currentModule.value.lessons.length === 0
      ) {
        emit('next-lesson-action')
        return
      }

      emit('module-action')
      return
    }

    loadingShopifyCheckout.value = true
    await coursePurchaseIntended(currentCourseId.value, course.value!.title, 'selector_cta')
    await createShopifyCheckout()

    setTimeout(() => {
      loadingShopifyCheckout.value = false
    }, 2500)
  }

  const toggleModule = (moduleId: string) => {
    if (isModuleOpen.value(moduleId)) {
      moduleOpenStatus.open = false
      return
    }

    moduleOpenStatus.moduleId = moduleId
    moduleOpenStatus.open = true
  }

  const isModuleOpen = computed(() => {
    return (moduleId: string) => {
      return (
        moduleOpenStatus.moduleId === moduleId &&
        moduleOpenStatus.open &&
        ((inEditorMode.value && !currentEditorModule.value?.disabled) || !currentModule.value.disabled)
      )
    }
  })

  const moduleHasQuiz = computed(() => {
    return (module: CourseModuleState) => {
      return module.lessons.some((lesson) => {
        return lesson.type === 'Quiz'
      })
    }
  })

  const passedModuleQuizzes = computed(() => {
    return (module: CourseModuleState) => {
      const lessonQuizzes = module.lessons.filter((lesson) => {
        return lesson.type === 'Quiz'
      })
      return lessonQuizzes.every((lesson) => {
        return lesson.quizMeta?.passed
      })
    }
  })

  const passedLesson = computed(() => {
    return (lesson: Lesson) => {
      if (!lesson.quizMeta) return false
      return lesson.quizMeta?.passed
    }
  })

  const lessonQuizStrokeColor = computed(() => {
    return (lesson: Lesson) => {
      return passedLesson.value(lesson) ? 'var(--primary-color)' : 'var(--primary-text-color)'
    }
  })

  const moduleQuizStrokeColor = computed(() => {
    return (module: CourseModuleState) => {
      return passedModuleQuizzes.value(module) ? 'var(--primary-color)' : 'var(--primary-text-color)'
    }
  })

  const passedAllQuizzes = computed(() => {
    return quizMetaCount.value.passedQuizCount === quizMetaCount.value.totalQuizCount
  })

  const inCourseModulePage = computed(() => {
    return route.name === 'CourseModule'
  })

  const moduleContentCount = computed(() => {
    const contentCount = {
      lessonsCount: 0,
      moduleDuration: 0,
    }
    if (!filteredCourseModules.value) return contentCount
    filteredCourseModules.value.forEach((module) => {
      contentCount.moduleDuration += guidedQuizDuration.value(module.guidedQuiz?.numberOfQuestions || 0)
      module.lessons.forEach((lesson) => {
        contentCount.moduleDuration += lesson.duration || 0
      })
      contentCount.lessonsCount += module.lessons.length
    })
    return contentCount
  })

  const completedProgressCount = computed(() => {
    return (module: CourseModuleState) => {
      let count = 0
      const completedLessonsCount = module.lessons.filter((lesson) => lesson.completed && lesson.type !== 'Quiz').length
      module.lessons.find((lesson) => {
        return lesson.quizMeta?.passed ? count++ : ''
      })
      return `${count + completedLessonsCount}/${module.lessons.filter((l) => l.type !== 'Fake').length}`
    }
  })

  const courseAndModulesReady = computed(() => !!(course.value && courseModules.value))

  const showCourseExamDate = computed(
    () =>
      course.value?.exam &&
      course.value?.purchased &&
      (isSachkundeCompany.value || isTauresCompany.value) &&
      !inEditorMode.value,
  )

  const totalDownloadMaterials = computed(() => {
    return course.value?.downloadables?.length || 0
  })

  const hasCertificateUrl = computed(() => !!course.value?.certificateTemplateId)

  const formattedGuidedQuizDuration = computed(() => {
    return (module: CourseModuleState) => {
      return String(guidedQuizDuration.value(module.guidedQuiz!.numberOfQuestions)).replace('.', ',')
    }
  })

  const isLessonDropTarget = computed(() => {
    return (lid: string) => {
      return (
        inEditorMode.value &&
        lid === dropLessonTarget.value &&
        (isDraggingQuestion.value || isDraggingLesson.value) &&
        !(isDraggingQuestion.value && dropLessonTarget.value === lessonId.value)
      )
    }
  })

  const isModuleDropTarget = computed(() => {
    return (moduleId: string) => {
      const module = filteredCourseModules.value![dragModuleSourceIndex.value]
      const prevModule = filteredCourseModules.value![dragModuleSourceIndex.value - 1]

      return (
        inEditorMode.value &&
        isDraggingModule.value &&
        dropModuleTarget.value?.id === moduleId &&
        module?.id !== moduleId &&
        prevModule?.id !== moduleId
      )
    }
  })

  const isDraggingModule = computed(() => dragModuleSourceIndex.value >= 0)

  const isDraggingLesson = computed(() => dragLessonSourceIndex.value >= 0)

  const routeToModule = (module: CourseModuleState) => {
    if (moduleId.value !== module.id || !inCourseModulePage.value) {
      router
        .push({
          name: 'CourseModule',
          params: { courseId: currentCourseId.value, moduleId: module.id },
        })
        .catch(() => {
          return
        })

      if (moduleId.value !== module.id) {
        toggleModule(module.id)
      }
    }

    if (isMobileDevice.value) emit('show-content')
  }

  const routeToLesson = async (lesson: Lesson, moduleId: string, moduleIsFree: boolean) => {
    if (isLockedModule.value(moduleIsFree)) {
      await createShopifyCheckout()
      return
    }

    if (lessonId.value !== lesson.id) {
      if (lesson.quizMeta?.state === 'completed') {
        router
          .push({
            name: 'LessonQuizResults',
            params: { moduleId: moduleId, lessonId: lesson.id },
            query: { completionId: lesson.quizMeta?.completionId },
          })
          .catch(() => {
            return
          })
      } else {
        CourseModule.setCurrentLessonTitle(lesson.title)
        if (isSpecificLessonNew.value(lesson.id)) {
          CourseModule.resetCurrentLesson()
          CourseModule.setCurrentLesson(lesson)
        }
        router.push({ name: 'Lesson', params: { lessonId: lesson.id, moduleId: moduleId } }).catch(() => {
          return
        })
      }
    }
    if (isMobileDevice.value) emit('show-content')
  }

  const setActiveLesson = () => {
    const currentLesson = currentModule.value.lessons.find((lesson) => lesson.id === lessonId.value)
    if (currentLesson) {
      CourseModule.setCurrentLessonTitle(currentLesson.title)
    }
  }

  const setModuleCourseGroup = () => {
    const currentCourseModule = courseModules.value?.find((mod) => mod.id === moduleId.value)
    const courseGroup =
      course.value?.courseGroups?.find((group) => {
        return group.id === currentCourseModule?.courseGroupId
      }) || null
    if (courseGroup?.id) {
      selectedCourseGroup.value = courseGroup.id
    } else if (!selectedCourseGroup.value) {
      selectedCourseGroup.value = course.value?.courseGroups?.[0]?.id || ''
    }
    emit('current-course-group-id', selectedCourseGroup.value)
  }

  const openBrowserExtensionModal = () => {
    eventBus.$emit('show-modal', {
      modalContentComponent: 'BrowserExtensionModal',
      modalProps: {
        courseId: currentCourseId.value,
        moduleId: moduleId.value,
        lessonId: lessonId.value,
      },
      modalTitle: translateString('browserExtension.modal.title'),
      cssClass: 'browser-extention-modal-wrapper',
      modalCloseCallback: (callback: () => void) => {
        if (typeof callback === 'function') callback()
      },
    })
  }

  const openSpeakToExpertModal = () => {
    eventBus.$emit('show-modal', {
      modalContentComponent: 'SpeakToExpertModal',
      cssClass: 'speak-to-expert-modal',
      modalCloseCallback: (callback: () => void) => {
        if (typeof callback === 'function') callback()
      },
    })
  }

  const closeExtensionAdvert = () => {
    canAdvertiseExtension.value = false
    CookieHelper.setCookieValue('advertise_extension', 'true')
  }

  const onModuleDragStart = (index: number) => {
    dragModuleSourceIndex.value = index
  }

  const onModuleDragEnter = (module: CourseModuleState) => {
    if (module.id !== dropModuleTarget.value?.id) {
      const editorModule = EditorModule.coursesModuleMap[currentCourseId.value]?.find((m) => m.id === module.id)
      dropModuleTarget.value = editorModule || null
      if (isDraggingLesson.value) {
        const editorModule = EditorModule.coursesModuleMap[currentCourseId.value]?.find((m) => m.id === module.id)
        editorModule?.lessons.push(fakeLessonPayload)
      }
      if (isDraggingLesson.value || isDraggingQuestion.value) toggleModule(module.id)
    }
  }

  const onModuleDragEnd = () => {
    dragModuleSourceIndex.value = -1
    dropModuleTarget.value = null
    const index = EditorModule.coursesModuleMap[currentCourseId.value]?.findIndex((m) => m.id === '123')
    if (index !== undefined) EditorModule.coursesModuleMap[currentCourseId.value].splice(index, 1)
  }

  const onModuleDrop = () => {
    if (
      isDraggingModule.value &&
      !isDraggingLesson.value &&
      dragModuleSourceIndex.value >= 0 &&
      dropModuleTarget.value
    ) {
      const moduleToRemove = filteredCourseModules.value![dragModuleSourceIndex.value]
      // ignore drop if source and target module is the same
      if (moduleToRemove.id === dropModuleTarget.value.id) return

      const courseModules = EditorModule.coursesModuleMap[currentCourseId.value]
      const moduleMapCopy = structuredClone(
        EditorModule.coursesModuleMap[currentCourseId.value]?.filter((m) => m.id !== '123') || [],
      ) as CourseModuleState[]
      const moduleToRemoveIndex = courseModules.findIndex((mod) => mod.id === moduleToRemove.id)
      courseModules.splice(moduleToRemoveIndex, 1)

      const newModuleIndex = courseModules.findIndex((mod) => mod.id === dropModuleTarget.value?.id)
      courseModules.splice(newModuleIndex + 1, 0, moduleToRemove)

      eventBus.$emit('turn-on-loader')
      const apiPayload = courseModules
        .filter((m) => m.id !== '123')
        .map((m, index) => ({ moduleId: m.id, position: index }))
      CourseApi.reorderModules(currentCourseId.value, apiPayload)
        .then(async () => {
          eventBus.$toasted.success(translateString('editor.messages.modulesReorderedSuccessfully'))
          await CourseModule.getCourseModules(currentCourseId.value)
          cacheStateAndSubscribeToEditorEvents(
            'modules-reordering-',
            moduleMapCopy,
            apiPayload,
            moduleOrderingUndoCallback,
            moduleOrderingRedoCallback,
          )
        })
        .catch((e) => {
          if (e.response?.status === 403) {
            eventBus.$toasted.error(translateString('editor.messages.onlyEditorsAllowed'))
          }
        })
        .finally(() => {
          eventBus.$emit('turn-off-loader')
        })
    }
  }

  const moduleOrderingUndoCallback = (data: EditorStatePayload) => {
    eventBus.$emit('turn-on-loader')
    const apiPayload = (data.value as CourseMap).modules.map((m, index) => ({
      moduleId: m.id,
      position: index,
    }))
    CourseApi.reorderModules((data.value as CourseMap).courseId, apiPayload)
      .then(async () => {
        eventBus.$toasted.success(translateString('editor.messages.modulesReorderedSuccessfully'))
        await CourseModule.getCourseModules((data.value as CourseMap).courseId)
        const curModules = structuredClone(courseModules.value || []) as CourseModuleState[]
        EditorModule.setCourseModules({
          courseId: (data.value as CourseMap).courseId,
          modules: curModules,
        })
      })
      .finally(() => {
        eventBus.$emit('turn-off-loader')
      })
  }

  const moduleOrderingRedoCallback = (data: EditorStatePayload) => {
    eventBus.$emit('turn-on-loader')
    CourseApi.reorderModules(
      (data.value as CourseMap).courseId,
      data.apiPayload as { moduleId: string; position: number }[],
    )
      .then(async () => {
        eventBus.$toasted.success(translateString('editor.messages.modulesReorderedSuccessfully'))
        await CourseModule.getCourseModules((data.value as CourseMap).courseId)
        const curModules = structuredClone(courseModules.value || []) as CourseModuleState[]
        EditorModule.setCourseModules({
          courseId: (data.value as CourseMap).courseId,
          modules: curModules,
        })
      })
      .finally(() => {
        eventBus.$emit('turn-off-loader')
      })
  }

  const onLessonDragStart = (module: CourseModuleState, lessonIndex: number) => {
    const editorModule = EditorModule.coursesModuleMap[currentCourseId.value]?.find((m) => m.id === module.id)
    editorModule?.lessons.push(fakeLessonPayload)

    dragModuleSource.value = editorModule as CourseModuleState
    dragLessonSourceIndex.value = lessonIndex
  }

  const onLessonDragEnter = (module: CourseModuleState, lessonId: string) => {
    const editorModule = EditorModule.coursesModuleMap[currentCourseId.value]?.find((m) => m.id === module.id)
    dropModuleTarget.value = editorModule as CourseModuleState
    dropLessonTarget.value = lessonId
    eventBus.$emit('question-drag', editorModule, lessonId)
  }

  const onLessonDragEnd = (module: CourseModuleState) => {
    // param module is the same as dragModuleSource
    let editorModule = EditorModule.coursesModuleMap[currentCourseId.value]?.find((m) => m.id === module.id)
    let fakeIndex = editorModule?.lessons.findIndex((l) => l.type === 'Fake')
    if (typeof fakeIndex === 'number' && fakeIndex >= 0) editorModule?.lessons.splice(fakeIndex, 1)

    editorModule = EditorModule.coursesModuleMap[currentCourseId.value]?.find(
      (m) => m.id === dropModuleTarget.value?.id,
    )
    fakeIndex = editorModule?.lessons.findIndex((l) => l.type === 'Fake')
    if (typeof fakeIndex === 'number' && fakeIndex >= 0) editorModule?.lessons.splice(fakeIndex, 1)

    dropLessonTarget.value = ''
    dragLessonSourceIndex.value = -1
    dropModuleTarget.value = null
    dragModuleSource.value = null
  }

  const onLessonDrop = () => {
    if (isDraggingQuestion.value) {
      eventBus.$emit('lesson-drop')
      return
    }

    if (isDraggingLesson.value && dragLessonSourceIndex.value >= 0 && dropLessonTarget.value) {
      const moduleMapCopy = structuredClone(
        EditorModule.coursesModuleMap[currentCourseId.value] || [],
      ) as CourseModuleState[]
      const newIndex = dropModuleTarget.value?.lessons.findIndex((lesson) => lesson.id === dropLessonTarget.value)
      if (dragLessonSourceIndex.value === newIndex) return

      const removedLesson = dragModuleSource.value?.lessons.splice(dragLessonSourceIndex.value, 1)
      if (typeof newIndex === 'number' && newIndex > -1 && removedLesson) {
        dropModuleTarget.value?.lessons.splice(newIndex, 0, removedLesson[0])
      }

      const apiPayload: { moduleId: string; lessons: { id: string; position: number }[] }[] = []
      let movedFromAnotherModule = false
      const replaceModuleId = dropModuleTarget.value?.id

      if (dragModuleSource.value?.id !== dropModuleTarget.value?.id) {
        movedFromAnotherModule = true
        apiPayload.push({
          moduleId: dragModuleSource.value!.id,
          lessons: dragModuleSource
            .value!.lessons.filter((l) => l.type !== 'Fake')
            .map((lesson, index) => ({ id: lesson.id, position: index })),
        })
      }

      apiPayload.push({
        moduleId: dropModuleTarget.value!.id,
        lessons: dropModuleTarget
          .value!.lessons.filter((l) => l.type !== 'Fake')
          .map((lesson, index) => ({ id: lesson.id, position: index })),
      })

      eventBus.$emit('turn-on-loader')
      CourseApi.reorderLessons(apiPayload)
        .then(async () => {
          eventBus.$toasted.success(translateString('editor.messages.lessonReorderedSuccessfully'))
          await CourseModule.getCourseModules(currentCourseId.value)
          patchCourseModules()
          cacheStateAndSubscribeToEditorEvents(
            'lessons-reordering-',
            moduleMapCopy,
            apiPayload,
            lessonOrderingUndoCallback,
            lessonOrderingRedoCallback,
          )
        })
        .catch((e) => {
          if (e.response?.status === 403) {
            eventBus.$toasted.error(translateString('editor.messages.onlyEditorsAllowed'))
          }
        })
        .finally(() => {
          eventBus.$emit('turn-off-loader')
          if (movedFromAnotherModule && removedLesson?.[0].id === lessonId.value && replaceModuleId) {
            router.replace({
              name: 'Lesson',
              params: { moduleId: replaceModuleId },
            })
          }
        })
    }
  }

  const patchCourseModules = () => {
    const curModules = structuredClone(courseModules.value || []) as CourseModuleState[]
    CourseModule.setCurrentModule(currentModule.value as CourseModuleState)
    EditorModule.setCourseModules({ courseId: currentCourseId.value, modules: curModules })
    EditorModule.setCurrentModuleLessons(structuredClone(currentModule.value.lessons))
  }

  const cacheStateAndSubscribeToEditorEvents = (
    cacheKey: string,
    moduleMapCopy: CourseModuleState[],
    apiPayload: {
      moduleId: string
      position?: number
      lessons?: { id: string; position: number }[]
    }[],
    discardCallback: (data: EditorStatePayload) => void,
    saveCallback: (data: EditorStatePayload) => void,
  ) => {
    const key = cacheKey + uuidv4()
    EditorModule.addEditorChange({
      key,
      path: 'module-selector',
      skipGrouping: true,
      value: {
        courseId: currentCourseId.value,
        modules: moduleMapCopy,
      },
      apiPayload,
    })
    EditorModule.subscribe({
      type: 'discard',
      key,
      callback: discardCallback,
    })
    EditorModule.subscribe({
      type: 'save',
      key,
      callback: saveCallback,
    })
  }

  const lessonOrderingUndoCallback = (data: EditorStatePayload) => {
    eventBus.$emit('turn-on-loader')
    const apiPayload = (data.apiPayload! as any).map((payload: any) => {
      const module = (data.value as CourseMap).modules.find((m) => m.id === payload.moduleId)
      return {
        moduleId: payload.moduleId,
        lessons:
          module?.lessons
            .filter((l) => l.type !== 'Fake')
            .map((lesson, index) => ({ id: lesson.id, position: index })) || [],
      }
    })
    CourseApi.reorderLessons(apiPayload)
      .then(async () => {
        eventBus.$toasted.success(translateString('editor.messages.lessonReorderedSuccessfully'))
        await CourseModule.getCourseModules((data.value as CourseMap).courseId)
        patchCourseModules()
      })
      .finally(() => {
        eventBus.$emit('turn-off-loader')
      })
  }

  const lessonOrderingRedoCallback = (data: EditorStatePayload) => {
    eventBus.$emit('turn-on-loader')
    CourseApi.reorderLessons(data.apiPayload! as any)
      .then(async () => {
        eventBus.$toasted.success(translateString('editor.messages.lessonReorderedSuccessfully'))
        await CourseModule.getCourseModules((data.value as CourseMap).courseId)
        patchCourseModules()
      })
      .finally(() => {
        eventBus.$emit('turn-off-loader')
      })
  }

  const discardNewLessonChangesCallback = async () => {
    if (isNewLesson.value) {
      await CourseModule.resetCurrentLesson()
      const courseModule = CourseModule.coursesModuleMap[currentCourseId.value]!.find(
        (m) => m.id === moduleId.value,
      ) as CourseModuleState
      const index = courseModule.lessons.findIndex((l) => l.id === lessonId.value)
      courseModule.lessons.splice(index, 1)
      await addNewLesson()
    }
  }

  const addNewLesson = async (modId?: string) => {
    const payload = Object.assign({}, fakeLessonPayload)
    payload.id = isNewLesson.value ? lessonId.value : 'new-' + uuidv4()
    payload.moduleId = modId || moduleId.value
    payload.title = translateString('editor.newLessonTitle')
    payload.type = 'Text'

    const courseModule = CourseModule.coursesModuleMap[currentCourseId.value]!.find(
      (m) => m.id === modId || moduleId.value,
    ) as CourseModuleState
    courseModule.lessons.push(payload)
    CourseModule.resetCurrentLesson()
    CourseModule.setCurrentLesson(payload)
    if (!isNewLesson.value) {
      router.push({ name: 'Lesson', params: { lessonId: payload.id, moduleId: payload.moduleId } }).catch(() => {
        return
      })
    }
  }

  const addNewModule = async () => {
    const payload = Object.assign({}, fakeModulePayload)
    payload.id = isNewModule.value ? moduleId.value : 'mod-' + uuidv4()
    CourseModule.coursesModuleMap[currentCourseId.value]?.push(payload)
    CourseModule.setCurrentModule(currentModule.value as CourseModuleState)
    if (!isNewModule.value) {
      router
        .push({
          name: 'CourseModule',
          params: { courseId: currentCourseId.value, moduleId: payload.id },
        })
        .catch(() => {
          return
        })
    }
  }

  const showAddCourseGroupModal = () => {
    eventBus.$emit('show-modal', {
      modalContentComponent: 'CreateOrUpdateCourseGroupModal',
      modalProps: {
        courseId: currentCourseId.value,
        addingNewCourseGroup: true,
      },
      cssClass: 'create-course-group-modal',
      modalCloseCallback: (callback: () => void) => {
        if (typeof callback === 'function') callback()
      },
    })
  }

  const showEditOrDeleteCourseGroupModal = (
    courseGroupId: string,
    _courseGroupTitle: string,
    deleteCourseGroup: boolean,
  ) => {
    eventBus.$emit('show-modal', {
      modalContentComponent: 'EditOrDeleteCourseGroupModal',
      modalProps: {
        courseGroupId,
        _courseGroupTitle,
        deleteCourseGroup,
      },
      cssClass: 'edit-or-delete-course-group-modal',
      modalCloseCallback: (callback: () => void) => {
        if (typeof callback === 'function') callback()
      },
    })
  }

  const editCourseGroup = (payload: { selectable: boolean; type: string; title: string }) => {
    showEditOrDeleteCourseGroupModal(payload.type, payload.title, false)
  }

  const deleteCourseGroup = (payload: { selectable: boolean; type: string; title: string }) => {
    showEditOrDeleteCourseGroupModal(payload.type, payload.title, true)
  }

  watch(moduleId, (newModuleId) => {
    if (!isModuleOpen.value(newModuleId)) {
      toggleModule(newModuleId)
    }
    setModuleCourseGroup()
  })

  watch(lessonId, (newLessonId: string, oldLessonId: string) => {
    if (!oldLessonId && newLessonId) {
      setModuleCourseGroup()
    }
  })

  watch(courseAndModulesReady, (value: boolean) => {
    if (value) {
      setModuleCourseGroup()
    }
  })

  watch(selectedCourseGroup, (newCourseGroupId: string, oldCourseGroupId: string) => {
    if (oldCourseGroupId && !route.query.modal) {
      if (!CourseModule.courseModules(currentCourseId.value, newCourseGroupId).length && inEditorMode.value) {
        addNewModule()
      } else {
        if (!moduleId.value) return
        moduleOpenStatus.moduleId = nextEnabledModule.value(currentCourseId.value, newCourseGroupId)?.id || ''
        moduleOpenStatus.open = true
        const lesson = nextLesson(newCourseGroupId)
        if (nextLessonIndex(newCourseGroupId) === 0 || !lesson) {
          router.push({ name: 'CourseModule', params: { moduleId: moduleOpenStatus.moduleId } }).catch(() => {
            return
          })
        } else {
          router
            .push({
              name: 'Lesson',
              params: { moduleId: moduleOpenStatus.moduleId, lessonId: lesson!.id },
            })
            .catch(() => {
              return
            })
        }
      }
    }
    const { query } = route
    if (query.modal) router.replace({ query: { ...query, modal: undefined } })
    CourseModule.setCurrentCourseGroup(newCourseGroupId)
  })

  watch(currentCourseGroupId, (value) => {
    if (value) {
      selectedCourseGroup.value = value
    }
  })

  watch(inEditorMode, (value) => {
    if (!value && !currentModule.value.isFree && !course.value?.purchased && onLessonPage.value) {
      // we route to the currentModule since the lessons in it are locked and the course is not purchased
      routeToModule(currentModule.value as CourseModuleState)
    }
  })

  const canAdvertiseExtension = ref(true)
  onMounted(() => {
    EditorModule.subscribe({
      type: 'discard',
      key: 'new-lesson-changes',
      callback: discardNewLessonChangesCallback,
    })

    if (inEditorMode.value) {
      if (isNewModule.value) {
        addNewModule()
      } else if (isNewLesson.value) {
        addNewLesson()
      }
    }

    eventBus.$on('question-drag-start', () => {
      isDraggingQuestion.value = true
    })

    eventBus.$on('question-drag-end', () => {
      dropModuleTarget.value = null
      dropLessonTarget.value = ''
      isDraggingQuestion.value = false
    })

    canAdvertiseExtension.value = !CookieHelper.getCookieValue('advertise_extension')
    // Both course and course modules endpoints need to respond almost at the same time so we can set the correct
    // courseGroupId. But sometimes, one of the endpoints is faster and that leads to not setting the correct courseGroupId.
    // The solution is to either set the initial courseGroupId when the endpoints respond at the same time or watch by waiting
    // for both endpoints to be ready.
    if (course.value && courseModules.value) {
      setModuleCourseGroup()
    }

    toggleModule(currentModule.value.id)
    setActiveLesson()
  })

  onBeforeUnmount(() => {
    EditorModule.unsubscribe({ type: 'discard', callback: discardNewLessonChangesCallback })
    EditorModule.unsubscribe({ type: 'discard', callback: lessonOrderingUndoCallback })
    EditorModule.unsubscribe({ type: 'save', callback: lessonOrderingRedoCallback })
  })

return { __sfc: true,emit, route, router, translateString, moduleIcon, durationIcon, arrowRight, arrowDown, open, lessonCompletedIcon, activeLessonIcon, quizIcon, courseCertIconWhite, bigDownloadIcon, lockIcon, michaelAvatar, phoneIcon, pencilIcon, chrome, edge, closeIcon, plusIcon, formatDurationToHHMM, convertToRoman, isSachkundeCompany, isTauresCompany, isDesktopDevice, isLargeDesktop, isMobileDevice, createShopifyCheckout, course, currentCourseId, moduleId, lessonId, currentModule, currentLesson, courseModules, studyingStatus, quizMetaCount, isPurchasedCourse, isTrialCourse, guidedQuizDuration, nextEnabledModule, nextLesson, nextLessonIndex, fetchingEditorDependencies, showNextCTA, nextSiblingModule, nextCourseModule, onLessonPage, isTrialExpired, coursePurchaseIntended, inEditorMode, isNewLesson, isNewModule, currentEditorModule, moduleOpenStatus, dropModuleTarget, dragModuleSource, dropLessonTarget, dragLessonSourceIndex, dragModuleSourceIndex, isDraggingQuestion, loadingShopifyCheckout, hoveringIndex, fakeModulePayload, fakeLessonPayload, ctaStatus, shouldBuyCourse, isLockedModule, currentCourseGroupId, selectedCourseGroup, courseGroupDropdownItems, filteredCourseModules, moduleCount, hasSomeFreeModules, showTalkToExpert, isSpecificModuleNew, isSpecificLessonNew, showStartExamCTA, selectorAction, toggleModule, isModuleOpen, moduleHasQuiz, passedModuleQuizzes, passedLesson, lessonQuizStrokeColor, moduleQuizStrokeColor, passedAllQuizzes, inCourseModulePage, moduleContentCount, completedProgressCount, courseAndModulesReady, showCourseExamDate, totalDownloadMaterials, hasCertificateUrl, formattedGuidedQuizDuration, isLessonDropTarget, isModuleDropTarget, isDraggingModule, isDraggingLesson, routeToModule, routeToLesson, setActiveLesson, setModuleCourseGroup, openBrowserExtensionModal, openSpeakToExpertModal, closeExtensionAdvert, onModuleDragStart, onModuleDragEnter, onModuleDragEnd, onModuleDrop, moduleOrderingUndoCallback, moduleOrderingRedoCallback, onLessonDragStart, onLessonDragEnter, onLessonDragEnd, onLessonDrop, patchCourseModules, cacheStateAndSubscribeToEditorEvents, lessonOrderingUndoCallback, lessonOrderingRedoCallback, discardNewLessonChangesCallback, addNewLesson, addNewModule, showAddCourseGroupModal, showEditOrDeleteCourseGroupModal, editCourseGroup, deleteCourseGroup, canAdvertiseExtension, computed, onBeforeUnmount, onMounted, reactive, ref, watch, KetchUpButton, LoadingSpinner, Badge, SVGRenderer, Dropdown, Drag, StartCourseExamCTA, EditorButtons, CourseSearch, CourseExamDate, GoogleReviewWidget, useRoute, useRouter, useIcons, useDuration, useCommonMixin, useCourse, useSegment, useI18n, useBreakpoint, useEditor, useShopifyClient, CourseModule, EditorModule, CourseApi, uuidv4, CookieHelper, eventBus }
}

})